/**
 * Назначение: рабочий стол/сводка по доступным разделам и предупреждениям.
 * Кто: все сотрудники (tenant); СА в контексте организации.
 * Вход: Сайдбар: Главная / Рабочий стол; редиректы с /tenant/dashboard и /fitness/dashboard.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ROUTE_PATHS } from '../../app/router/route-paths';
import { fetchOrgDashboardWarnings } from '../../features/organization-dashboard-warnings/api/org-dashboard-warnings.api';
import type { OrgDashboardWarningItem } from '../../features/organization-dashboard-warnings/api/org-dashboard-warnings.types';
import { useAuth } from '../../shared/auth/use-auth';
import StaffDashboardTrainerTodayScheduleCard from './StaffDashboardTrainerTodayScheduleCard';
import TenantOrgOperationalDashboard from './TenantOrgOperationalDashboard';
import { isOrgTenantDashboardOpsAudience } from './tenant-org-dashboard-ops-audience';
import { userFacingLoadError } from '../../shared/lib/staff-user-facing-error';
import { Alert } from '../../shared/ui/Alert';
import { Badge } from '../../shared/ui/Badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../shared/ui/Card';
import { Container } from '../../shared/ui/Container';
import { Spinner } from '../../shared/ui/Spinner';
import { Stack } from '../../shared/ui/Stack';

const ORG_STAFF_CAPABILITY_DASHBOARD_PATHS: readonly string[] = [ROUTE_PATHS.tenantDashboard, ROUTE_PATHS.staffDashboard];

function isOrgStaffCapabilityDashboardPath(path: string): boolean {
  return ORG_STAFF_CAPABILITY_DASHBOARD_PATHS.includes(path);
}

function warningAlertVariant(
  s: OrgDashboardWarningItem['severity']
): 'warning' | 'error' | 'info' {
  if (s === 'danger') return 'error';
  if (s === 'info') return 'info';
  return 'warning';
}

function severityBadgeVariant(s: OrgDashboardWarningItem['severity']): 'neutral' | 'warning' | 'danger' {
  if (s === 'danger') return 'danger';
  if (s === 'info') return 'neutral';
  return 'warning';
}

/** Экраны дашборда по capability-маршрутам (`routes_allowed`). Расширенная сводка tenant при контексте орг., см. `isOrgTenantDashboardOpsAudience`. */
export default function StaffCapabilityDashboardPage() {
  const path = useLocation().pathname.replace(/\/+$/, '') || '/';

  const { apiClient, me } = useAuth();
  const showTenantOps =
    path === ROUTE_PATHS.tenantDashboard && isOrgTenantDashboardOpsAudience(me);
  const loadWarningsSurface = isOrgStaffCapabilityDashboardPath(path);
  const isTrainerRole = (me?.role ?? '').trim().toLowerCase() === 'trainer';
  /** Предупреждения на tenant/staff dashboard для роли `trainer` на бэкенде запрещены — не дергаем API. */
  const warningsFetchEnabled = loadWarningsSurface && !isTrainerRole;

  const [warningsLoading, setWarningsLoading] = useState(warningsFetchEnabled);
  const [warningsError, setWarningsError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<OrgDashboardWarningItem[]>([]);

  const reloadWarnings = useCallback(async () => {
    if (!warningsFetchEnabled) return;
    setWarningsLoading(true);
    setWarningsError(null);
    try {
      const res = await fetchOrgDashboardWarnings(apiClient);
      setWarnings(res.warnings);
    } catch (e) {
      setWarnings([]);
      setWarningsError(userFacingLoadError(e));
    } finally {
      setWarningsLoading(false);
    }
  }, [apiClient, warningsFetchEnabled]);

  useEffect(() => {
    if (!warningsFetchEnabled) {
      setWarnings([]);
      setWarningsError(null);
      setWarningsLoading(false);
      return;
    }
    void reloadWarnings();
  }, [warningsFetchEnabled, reloadWarnings]);

  const warningsBlock = useMemo(() => {
    if (!warningsFetchEnabled) return null;
    return (
      <Card data-testid="org-dashboard-warnings">
        <CardHeader>
          <CardTitle>Предупреждения</CardTitle>
          <CardDescription>Операционные проверки организации: филиалы, оплата и связанные настройки.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {warningsLoading ? (
            <div data-testid="org-dashboard-warnings-loading">
              <Spinner label="Загрузка предупреждений…" />
            </div>
          ) : null}
          {!warningsLoading && warningsError ? (
            <div data-testid="org-dashboard-warnings-error">
              <Alert variant="error" title="Не удалось загрузить предупреждения">
                {warningsError}
              </Alert>
            </div>
          ) : null}
          {!warningsLoading && !warningsError && warnings.length === 0 ? (
            <p
              className="staff-m-0 text-sm text-[hsl(var(--ui-muted-foreground))]"
              data-testid="org-dashboard-warnings-empty"
            >
              Критичных предупреждений нет — данные организации в порядке по проверяемым пунктам.
            </p>
          ) : null}
          {!warningsLoading && !warningsError && warnings.length > 0
            ? warnings.map((w) => (
                <div key={w.code} data-testid="org-dashboard-warnings-item" data-warning-code={w.code}>
                  <Alert variant={warningAlertVariant(w.severity)} title={w.title}>
                    <div className="flex flex-col gap-2">
                      <div>
                        <Badge variant={severityBadgeVariant(w.severity)} badgeSize="sm" className="font-normal uppercase">
                          {w.severity}
                        </Badge>
                      </div>
                      {w.detail ? <p className="staff-m-0 text-sm leading-snug">{w.detail}</p> : null}
                    </div>
                  </Alert>
                </div>
              ))
            : null}
        </CardContent>
      </Card>
    );
  }, [warningsFetchEnabled, warnings, warningsError, warningsLoading]);

  return (
    <Container maxWidth="lg">
      <Stack gap={4}>
        {warningsFetchEnabled ? warningsBlock : null}
        {loadWarningsSurface && isTrainerRole ? <StaffDashboardTrainerTodayScheduleCard /> : null}
        {showTenantOps ? <TenantOrgOperationalDashboard /> : null}
      </Stack>
    </Container>
  );
}
